import * as React from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
	getReplacedBrandIncludedBaggage,
	getGdsServicesBySegments,
	getIsCancelled
} from '../../../Checkout/store/order/selectors';
import { BaggageType, FlightFragment, OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { useTheme } from '../../../theme';
import { GdsAggregationServiceBaggage, GdsAggregationServiceGroup } from '../../../Checkout/types';
import BaggageCard from './Service/Baggage/Baggage';
import { Baggage } from '../../../BaggageService/types';
import Meal from './Service/Meal/Meal';
import Seat from './Service/Seat/Seat';
import { useTranslation } from 'react-i18next';
import DesktopFlightModalContentSelected from '../../../DesktopFlight/components/DesktopFlightModalContentSelected/DesktopFlightModalContentSelected';
import { getFlightWithSegmentGroup } from '../../../Checkout/components/Checkout/SelectedFlight/utils';
import { useToggleable } from '../../../hooks';
import { useMediaQuery } from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../utils';
import { DesktopFlightFullscreen } from '../../../DesktopFlight';
import { BaseButton } from '../../../BaseComponents';

interface Props {
	segmentId?: string;
	flight: FlightFragment;
	orderId: string;
}

const SERVICES_ORDER = {
	CarryOn: 4,
	[OrderAdditionalServiceGdsServiceServiceType.Baggage]: 3,
	[OrderAdditionalServiceGdsServiceServiceType.Seat]: 2,
	[OrderAdditionalServiceGdsServiceServiceType.Meal]: 1
};

const SegmentServices: React.FC<Props> = ({ segmentId, flight, orderId }) => {
	const { t } = useTranslation('FlightInfoCard');

	const { isOpen, open, close } = useToggleable(false);
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const cancelled = useSelector(getIsCancelled);

	const services = useSelector(getGdsServicesBySegments),
		brandIncludedBaggage = useSelector(getReplacedBrandIncludedBaggage),
		segmentGroup = flight.segmentGroups.find(group => group.segments.some(segment => segment.id === segmentId));

	const segmentServices = services.find(service => service.segmentId === segmentId);

	const includedSegmentServices = React.useMemo(
		() => brandIncludedBaggage.filter(({ baggage }) => baggage.includedSegments.includes(segmentId)),
		[brandIncludedBaggage, segmentId]
	);
	const theme = useTheme('FlightInfoCard').SegmentServices;

	const flightWithSegmentGroup = getFlightWithSegmentGroup(
		flight,
		flight.segmentGroups.find(group => group.segments.some(segment => segment.id === segmentId))
	);

	const separateSelectedBaggageByRfics = (
		baggageServiceGroup: GdsAggregationServiceGroup
	): GdsAggregationServiceGroup[] => {
		const groups: GdsAggregationServiceGroup[] = [];

		const baggageRficsMap = new Map<string, GdsAggregationServiceGroup>();

		baggageServiceGroup.selectedServices.forEach(baggage => {
			const baggageService = baggage.service as Baggage;

			if (baggageRficsMap.has(baggageService.rfics)) {
				baggageRficsMap.set(baggageService.rfics, {
					...baggageRficsMap.get(baggageService.rfics),
					selectedServices: [
						...baggageRficsMap.get(baggageService.rfics).selectedServices,
						{
							...baggage
						}
					]
				});
			} else {
				baggageRficsMap.set(baggageService.rfics, {
					...baggageServiceGroup,
					selectedServices: [
						{
							...baggage
						}
					],
					type:
						baggageService.type === BaggageType.CarryOn
							? 'CarryOn'
							: OrderAdditionalServiceGdsServiceServiceType.Baggage
				});
			}
		});

		baggageRficsMap.forEach(baggage => {
			groups.push(baggage);
		});

		return groups.length ? groups : [baggageServiceGroup];
	};

	const filteredServices = React.useMemo(() => {
		const services = new Map<string, GdsAggregationServiceGroup>();

		includedSegmentServices.forEach(({ baggage, count }) => {
			const selectedService = {
				count,
				service: { ...baggage, _serviceType: 'gdsService' as const },
				type: OrderAdditionalServiceGdsServiceServiceType.Baggage
			};
			const existing = services.get(baggage.rfics);

			if (existing) {
				services.set(baggage.rfics, {
					...existing,
					selectedServices: [...existing.selectedServices, selectedService]
				});
			} else {
				services.set(baggage.rfics, {
					type:
						baggage.type === BaggageType.CarryOn
							? 'CarryOn'
							: OrderAdditionalServiceGdsServiceServiceType.Baggage,
					services: [],
					selectedServices: [selectedService],
					canAddService: baggage.canBeAdded
				});
			}
		});

		segmentServices.services
			.filter(service => {
				return (
					SERVICES_ORDER.hasOwnProperty(service.type) &&
					(isMobile ||
						service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage ||
						!!service.selectedServices.length)
				);
			})
			.forEach(service => {
				if (service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage) {
					const selectedServices = service.selectedServices.filter(selected => {
						const baggage = selected.service as Baggage;

						/** Фильтруем все включенные услуги, т.к. они добавляются отдельно через brandIncludedBaggage */
						return baggage.included === 0 || !baggage.includedSegments?.length;
					});

					separateSelectedBaggageByRfics({ ...service, selectedServices }).forEach(splitGroup => {
						const rfics = (splitGroup.selectedServices[0]?.service as Baggage)?.rfics;

						if (rfics && !services.has(rfics)) {
							services.set(rfics, splitGroup);
						}
					});
				} else {
					const serviceRfisc = (service.selectedServices[0]?.service as GdsAggregationServiceBaggage)?.rfics;

					if (serviceRfisc && !services.has(serviceRfisc)) {
						services.set(serviceRfisc, service);
					}
				}
			});

		return Array.from(services.values()).sort((a, b) => SERVICES_ORDER[b.type] - SERVICES_ORDER[a.type]);
	}, [segmentServices, isMobile, includedSegmentServices]);

	const renderService = useCallback((service: GdsAggregationServiceGroup) => {
		const type = service.type;
		const serviceKey = (service.selectedServices[0]?.service as Baggage)?.rfics ?? type;

		switch (type) {
			case 'CarryOn':
			case OrderAdditionalServiceGdsServiceServiceType.Baggage:
				return <BaggageCard services={service} key={serviceKey} />;

			case OrderAdditionalServiceGdsServiceServiceType.Meal:
				return <Meal services={service} key={serviceKey} />;

			case OrderAdditionalServiceGdsServiceServiceType.Seat:
				return <Seat services={service} key={serviceKey} />;

			default:
				return null;
		}
	}, []);

	const servicesContainerStyles = !isMobile
		? {
				gridTemplateColumns: new Array(filteredServices.length > 3 ? 3 : filteredServices.length)
					.fill('1fr')
					.join(' ')
		  }
		: null;

	return (
		<div className={theme.container}>
			<div className={theme.wrapper}>
				<div className={theme.fareName}>
					{t('Fare')} {segmentGroup?.fareFamily?.title}
				</div>

				<BaseButton variant="text" onClick={open}>
					{t('Details')}
				</BaseButton>
			</div>

			{!cancelled && (
				<div className={theme.services} style={servicesContainerStyles}>
					{filteredServices.map(renderService)}
				</div>
			)}

			<DesktopFlightFullscreen isOpen={isOpen} onClose={close} isSelected={true}>
				<DesktopFlightModalContentSelected
					orderId={orderId}
					flightId={flightWithSegmentGroup.id}
					flight={flightWithSegmentGroup}
					onClose={close}
					lastSelectedFare={flightWithSegmentGroup.pricesForFareGroups?.[0]?.fareFamily}
				/>
			</DesktopFlightFullscreen>
		</div>
	);
};

export default SegmentServices;
